import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatIconModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatDialogModule, MatRadioModule, MatCheckboxModule, MatTabsModule, MatExpansionModule, MatProgressBarModule, MatButtonToggleModule, MatTableModule, MatSortModule, MatPaginatorModule, MatDatepickerModule, MatNativeDateModule } from '@angular/material';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FlexLayoutModule } from '@angular/flex-layout';
import {MatListModule} from '@angular/material/list';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { TopNavComponent } from './top-nav/top-nav.component';
import { BottomNavComponent } from './bottom-nav/bottom-nav.component';
import { AuthInterceptor } from './services/auth.interceptor';

import { LoginComponent } from './public/login/login.component';
import { LogoutTimerComponent } from './public/logoutTimer/logoutTimer.component';
import { MarketingLandingComponent } from './public/marketing-landing/marketing-landing.component';
import { ViewPropertyContainerComponent } from './public/view-property-container/view-property-container.component';
import { ResetPasswordComponent } from './public/reset-password/reset-password.component';

import { PropertyViewComponent } from './common/property-view/property-view.component';
import { OnePropertyViewComponent } from './common/one-property-view/one-property-view.component';
import { InvoicesComponent } from './common/invoices/invoices.component';
import { InvoiceViewComponent } from './common/invoice-view/invoice-view.component';
import { InvoicePdfComponent } from './common/invoice-pdf/invoice-pdf.component';
import { LeaseDocumentPdfComponent } from './common/lease-document-pdf/lease-document-pdf.component';
import { PdfCanvasViewerComponent } from './common/pdf-canvas-viewer/pdf-canvas-viewer.component';
import { MessageComponent } from './common/message/message.component';
import { AllMessagesComponent } from './common/all-messages/all-messages.component';

import { SuperAdminComponent } from './admin/super-admin/super-admin.component';
import { ManageLeasesComponent } from './admin/manage-leases/manage-leases.component';
import { PropertyExpensesComponent } from './admin/property-expenses/property-expenses.component';
import { ReceiptViewComponent } from './admin/receipt-view/receipt-view.component';
import { OrganizationExpensesComponent } from './admin/organization-expenses/organization-expenses.component';
import { OrgReceiptViewComponent } from './admin/org-receipt-view.component';
import { PropertyStatementsComponent } from './admin/property-statements/property-statements.component';
import { StatementViewComponent } from './admin/statement-view/statement-view.component';
import { OrganizationSettingsComponent } from './admin/organization-settings/organization-settings.component';
import { AdminLandingComponent } from './admin/admin-landing/admin-landing.component';
import { EditablePropertyContainerComponent } from './admin/editable-property-container/editable-property-container.component';
import { PropertyInternalDataComponent } from './admin/property-internal-data/property-internal-data.component';
import { MessageRespondComponent } from './admin/message-respond/message-respond.component';
import { AdminAllMessagesComponent } from './admin/admin-all-messages/admin-all-messages.component';
import { StartLeaseComponent } from './admin/start-lease/start-lease.component';
import { LeaseViewComponent } from './admin/lease-view/lease-view.component';
import { ModifyLeaseComponent } from './admin/modify-lease/modify-lease.component';
import { ExternalPaymentComponent } from './admin/external-payment/external-payment.component';
import { PaymentViewComponent } from './admin/payment-view/payment-view.component';
import { ContactInfoComponent } from './admin/contact-info/contact-info.component';
import { AdminInvoicesComponent } from './admin/admin-invoices/admin-invoices.component';
import { CreateUserComponent } from './admin/createUser/createUser.component';
import { GooglePlaceModule } from "ngx-google-places-autocomplete";

import { LesseeLandingComponent } from './lessee/lessee-landing/lessee-landing.component';
import { AccountSummaryComponent } from './lessee/account-summary/account-summary.component';
import { LesseePaymentComponent } from './lessee/lessee-payment/lessee-payment.component';
import { MatMenuModule, MatSelectModule, MatCardModule, MatTooltipModule, MatSidenavModule, MatDividerModule } from '@angular/material';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';

import { NgxLoadingModule } from 'ngx-loading';
import { AddresformComponent } from './public/test/addresform/addresform.component';
import { ReportsComponent } from './admin/reports/reports.component';
import { CivilDatePipe } from './shared/civil-date.pipe';
import { SidebarNavComponent } from './common/sidebar-nav/sidebar-nav.component';
import { OrgSwitcherComponent } from './common/org-switcher/org-switcher.component';
import { CreatePropertyDialogComponent } from './admin/create-property-dialog/create-property-dialog.component';
import { PropertyContextComponent } from './common/property-context/property-context.component';
import { EditPropertyComponent } from './admin/edit-property/edit-property.component';
import { ArchivedPropertiesComponent } from './admin/archived-properties/archived-properties.component';
import { ViewLeaseDialogComponent } from './admin/view-lease-dialog/view-lease-dialog.component';

@NgModule({
  declarations: [
    CivilDatePipe,
    AppComponent,
    TopNavComponent,
    SidebarNavComponent,
    OrgSwitcherComponent,
    LoginComponent,
    LogoutTimerComponent,
    MarketingLandingComponent,
    AdminLandingComponent,
    EditablePropertyContainerComponent,
    PropertyViewComponent,
    ViewPropertyContainerComponent,
    PropertyInternalDataComponent,
    MessageRespondComponent,
    MessageComponent,
    AdminAllMessagesComponent,
    StartLeaseComponent,
    LeaseViewComponent,
    BottomNavComponent,
    ModifyLeaseComponent,
    InvoicesComponent,
    ExternalPaymentComponent,
    InvoiceViewComponent,
    InvoicePdfComponent,
    LeaseDocumentPdfComponent,
    PdfCanvasViewerComponent,
    PaymentViewComponent,
    ContactInfoComponent,
    ResetPasswordComponent,
    LesseeLandingComponent,
    AdminInvoicesComponent,
    AllMessagesComponent,
    AccountSummaryComponent,
    LesseePaymentComponent,
    OnePropertyViewComponent,
    CreateUserComponent,
    AddresformComponent,
    SuperAdminComponent,
    ManageLeasesComponent,
    PropertyExpensesComponent,
    OrganizationExpensesComponent,
    OrganizationSettingsComponent,
    ReceiptViewComponent,
    OrgReceiptViewComponent,
    PropertyStatementsComponent,
    StatementViewComponent,
    ReportsComponent,
    CreatePropertyDialogComponent,
    PropertyContextComponent,
    EditPropertyComponent,
    ArchivedPropertiesComponent,
    ViewLeaseDialogComponent
  ],
  entryComponents: [
    ManageLeasesComponent,
    ModifyLeaseComponent,
    ViewLeaseDialogComponent,
    PropertyExpensesComponent,
    PropertyStatementsComponent,
    OrganizationSettingsComponent,
    OrganizationExpensesComponent,
    OrgReceiptViewComponent,
    StatementViewComponent,
    CreatePropertyDialogComponent,
    StartLeaseComponent
  ],
  imports: [
    HttpClientModule,
    FormsModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    FlexLayoutModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatTabsModule,
    MatListModule,
    MatExpansionModule,
    MatProgressBarModule,
    MatButtonToggleModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    BrowserModule,
    BrowserAnimationsModule,
    MatMenuModule,
    GooglePlaceModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    NgxLoadingModule.forRoot({}),
    MatSelectModule,
    MatCardModule,
    MatSidenavModule,
    MatDividerModule,
    ReactiveFormsModule,
    PdfViewerModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    AdminLandingComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
